import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Plus } from 'lucide-react';

interface OrderEditDialogProps {
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOrderUpdated: () => void;
}

interface SubOrderEdit {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  delivery_cost: number;
  delivery_option: 'pickup_only' | 'delivery_only' | 'both';
  delivery_address: string | null;
  pickup_address: string | null;
  notes: string | null;
}

interface OrderEdit {
  id: string;
  total_amount: number;
  total_delivery_cost: number;
  sub_orders: SubOrderEdit[];
}

const OrderEditDialog = ({ orderId, isOpen, onClose, onOrderUpdated }: OrderEditDialogProps) => {
  const [orderData, setOrderData] = useState<OrderEdit | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orderId && isOpen) {
      fetchOrderData();
    }
  }, [orderId, isOpen]);

  const fetchOrderData = async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select('id, total_amount, total_delivery_cost')
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      const { data: subOrders, error: subOrdersError } = await supabase
        .from('sub_orders')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at');

      if (subOrdersError) throw subOrdersError;

      setOrderData({
        ...order,
        sub_orders: subOrders || []
      });
    } catch (error) {
      console.error('Error fetching order data:', error);
      toast.error("שגיאה", {
        description: "לא ניתן לטעון את פרטי ההזמנה",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSubOrder = (index: number, field: string, value: any) => {
    if (!orderData) return;

    const newSubOrders = [...orderData.sub_orders];
    newSubOrders[index] = { ...newSubOrders[index], [field]: value };

    // Recalculate total_price when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      newSubOrders[index].total_price = newSubOrders[index].quantity * newSubOrders[index].unit_price;
    }

    // Recalculate order totals
    const total_amount = newSubOrders.reduce((sum, sub) => sum + sub.total_price, 0);
    const total_delivery_cost = newSubOrders.reduce((sum, sub) => sum + sub.delivery_cost, 0);

    setOrderData({
      ...orderData,
      sub_orders: newSubOrders,
      total_amount,
      total_delivery_cost
    });
  };

  const removeSubOrder = (index: number) => {
    if (!orderData || orderData.sub_orders.length <= 1) {
      toast.error("שגיאה", {
        description: "לא ניתן למחוק את הפריט האחרון בהזמנה",
      });
      return;
    }

    const newSubOrders = orderData.sub_orders.filter((_, i) => i !== index);
    const total_amount = newSubOrders.reduce((sum, sub) => sum + sub.total_price, 0);
    const total_delivery_cost = newSubOrders.reduce((sum, sub) => sum + sub.delivery_cost, 0);

    setOrderData({
      ...orderData,
      sub_orders: newSubOrders,
      total_amount,
      total_delivery_cost
    });
  };

  const saveChanges = async () => {
    if (!orderData) return;

    setSaving(true);
    try {
      // Update main order
      const { error: orderError } = await supabase
        .from('orders')
        .update({
          total_amount: orderData.total_amount,
          total_delivery_cost: orderData.total_delivery_cost
        })
        .eq('id', orderData.id);

      if (orderError) throw orderError;

      // Update sub orders
      for (const subOrder of orderData.sub_orders) {
        const { error: subError } = await supabase
          .from('sub_orders')
          .update({
            product_name: subOrder.product_name,
            quantity: subOrder.quantity,
            unit_price: subOrder.unit_price,
            total_price: subOrder.total_price,
            delivery_cost: subOrder.delivery_cost,
            delivery_option: subOrder.delivery_option,
            delivery_address: subOrder.delivery_address,
            pickup_address: subOrder.pickup_address,
            notes: subOrder.notes
          })
          .eq('id', subOrder.id);

        if (subError) throw subError;
      }

      toast.success("הצלחה", {
        description: "ההזמנה עודכנה בהצלחה"
      });

      onOrderUpdated();
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error("שגיאה", {
        description: "לא ניתן לשמור את השינויים",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!orderData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">עריכת הזמנה</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Sub Orders */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">פריטי ההזמנה</h3>
              {orderData.sub_orders.map((subOrder, index) => (
                <div key={subOrder.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">פריט {index + 1}</h4>
                    {orderData.sub_orders.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSubOrder(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>שם המוצר</Label>
                      <Input
                        value={subOrder.product_name}
                        onChange={(e) => updateSubOrder(index, 'product_name', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>אופן אספקה</Label>
                      <Select
                        value={subOrder.delivery_option}
                        onValueChange={(value: 'pickup_only' | 'delivery_only' | 'both') => updateSubOrder(index, 'delivery_option', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pickup_only">איסוף עצמי</SelectItem>
                          <SelectItem value="delivery_only">משלוח</SelectItem>
                          <SelectItem value="both">גמיש</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>כמות</Label>
                      <Input
                        type="number"
                        value={subOrder.quantity}
                        onChange={(e) => updateSubOrder(index, 'quantity', parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <div>
                      <Label>מחיר ליחידה (₪)</Label>
                      <Input
                        type="number"
                        value={subOrder.unit_price}
                        onChange={(e) => updateSubOrder(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>

                    <div>
                      <Label>סה"כ מוצר (₪)</Label>
                      <Input
                        type="number"
                        value={subOrder.total_price}
                        onChange={(e) => updateSubOrder(index, 'total_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>

                    <div>
                      <Label>עלות משלוח (₪)</Label>
                      <Input
                        type="number"
                        value={subOrder.delivery_cost}
                        onChange={(e) => updateSubOrder(index, 'delivery_cost', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  {subOrder.delivery_option !== 'pickup_only' && (
                    <div>
                      <Label>כתובת למשלוח</Label>
                      <Input
                        value={subOrder.delivery_address || ''}
                        onChange={(e) => updateSubOrder(index, 'delivery_address', e.target.value)}
                      />
                    </div>
                  )}

                  {subOrder.delivery_option !== 'delivery_only' && (
                    <div>
                      <Label>כתובת איסוף</Label>
                      <Input
                        value={subOrder.pickup_address || ''}
                        onChange={(e) => updateSubOrder(index, 'pickup_address', e.target.value)}
                      />
                    </div>
                  )}

                  <div>
                    <Label>הערות</Label>
                    <Textarea
                      value={subOrder.notes || ''}
                      onChange={(e) => updateSubOrder(index, 'notes', e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Order Totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-lg font-semibold">
                <span>סה"כ מוצרים:</span>
                <span>₪{orderData.total_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>סה"כ משלוח:</span>
                <span>₪{orderData.total_delivery_cost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xl font-bold">
                <span>סה"כ לתשלום:</span>
                <span>₪{(orderData.total_amount + orderData.total_delivery_cost).toLocaleString()}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose}>
                ביטול
              </Button>
              <Button onClick={saveChanges} disabled={saving}>
                {saving ? 'שומר...' : 'שמור שינויים'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderEditDialog;
